'use client';

import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import PDFControls from 'components/BankStatements/PDFControls';

import { getPDFFileAction } from 'actions/bankStatements';
import { get } from 'lodash';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const PDFViewer = ({
  statementId,
  selectedTransactionId,
  onPageChange,
  jsonData,
  applicationId,
  bankProcessorKey,
  currentPage = 1,
}) => {
  const dispatch = useDispatch();
  const confidenceThreshold = useSelector((state) => get(state, 'bankStatements.settings.confidenceThreshold'));
  const [pdfDocument, setPdfDocument] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfidenceHighlights, setShowConfidenceHighlights] =
    useState(true);
  const [renderTask, setRenderTask] = useState(null);
  const canvasRef = useRef(null);
  const lastPageRef = useRef(currentPage);
  const renderMutexRef = useRef(false);
  const scrollTimerRef = useRef(null);

  // Load PDF document
  useEffect(() => {
    const loadPdf = async () => {
      setIsLoading(true);
      dispatch(getPDFFileAction({
        statementId,
        entityType: 'application',
        entityId: applicationId,
        filename: `${applicationId}/${statementId}/statement.pdf`,
        bankProcessorKey,
        success: async (pdfData) => {
          if (pdfData) {
            const loadingTask = pdfjsLib.getDocument(pdfData);
            const pdf = await loadingTask.promise;
  
            setPdfDocument(pdf);
            setTotalPages(pdf.numPages);
          }
          setIsLoading(false);
        },
        fail: (error) => {
          console.error('Error loading PDF:', error);
          setError('Failed to load PDF document');
          setIsLoading(false);
        },
      }))
    };

    loadPdf();
  }, [statementId]);

  const cleanupHighlights = () => {
    const existingHighlights = document.querySelectorAll('.highlights-container');
    // '.highlights-container, .transaction-indicator' // Add in case transaction indicator needs to be removed
    existingHighlights.forEach(highlight => highlight.remove());
  };

  // Handle manual page navigation
  const handlePageChange = (newPage) => {
    if (newPage === currentPage) return;
    if (newPage < 1 || newPage > totalPages) return;

    // Cancel current render task if any
    if (renderTask) {
      try {
        renderTask.cancel();
        setRenderTask(null); // Clear the task reference immediately
      } catch (err) {
        console.log('Error cancelling render task during page change:', err);
      }
    }

    // Clear existing highlights and indicators
    cleanupHighlights();
    const existingIndicators = document.querySelectorAll('.transaction-indicator');
    existingIndicators.forEach(indicator => indicator.remove());

    // Reset any render state
    renderMutexRef.current = false;

    // Now set the new page
    lastPageRef.current = newPage; // Update last page ref immediately
    onPageChange(newPage);
  };

  const scrollToTransactionIndicator = () => {
    // Debounce the scroll operation
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
    }

    scrollTimerRef.current = setTimeout(() => {
      const indicator = document.querySelector('.transaction-indicator');
      if (indicator) {
        // Get the container that has scrolling
        const scrollContainer = canvasRef.current
          && canvasRef.current
          && canvasRef.current.parentElement
          && canvasRef.current.parentElement.parentElement;
        if (scrollContainer) {
          const indicatorRect = indicator.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();

          // Only scroll if the indicator is not already visible
          const isVisible = indicatorRect.top >= containerRect.top
            && indicatorRect.bottom <= containerRect.bottom;

          if (!isVisible) {
            // Calculate the scroll position to center the indicator in the viewport
            const scrollY = indicatorRect.top
              + scrollContainer.scrollTop
              - containerRect.top
              - containerRect.height / 2
              + indicatorRect.height / 2;

            // Scroll smoothly to the transaction
            scrollContainer.scrollTo({
              top: scrollY,
              behavior: 'smooth',
            });

            // console.log('Scrolling to transaction indicator');
          } else {
            // console.log('Indicator already visible, not scrolling');
          }
        }
      }
    }, 100);
  };

  // Improved location normalization
  const normalizeLocation = (location) => {
    if (!location) return null;

    // Extract the polygon array from different possible structures
    let polygonArray = null;

    // Try to handle various JSON structures we might get
    if (location.polygon) {
      // First try - standard polygon property
      if (Array.isArray(location.polygon)) {
        polygonArray = location.polygon;
      } else if (location.polygon.$values) {
        // Try to extract from $values inside polygon
        polygonArray = location.polygon.$values;
      }
    } else if (location.Polygon) {
      // Try PascalCase variant
      if (Array.isArray(location.Polygon)) {
        polygonArray = location.Polygon;
      } else if (location.Polygon.$values) {
        polygonArray = location.Polygon.$values;
      }
    } else if (location.$values && Array.isArray(location.$values)) {
      // Try direct $values at top level (might be used for polygon itself)
      polygonArray = location.$values;
    }

    if (!polygonArray) {
      return null;
    }

    // Make sure we have a page number
    const pageNumber = location.pageNumber || location.PageNumber || 1;

    return {
      pageNumber,
      polygon: polygonArray,
    };
  };

  // Function to handle rectangle points
  const createPolygonElement = (
    polygonPoints,
    confidence,
    viewport
  ) => {
    if (!polygonPoints
      || !Array.isArray(polygonPoints)
      || polygonPoints.length < 8
      || confidenceThreshold === null
    ) {
      // Need at least 4 points (x1,y1,x2,y2,x3,y3,x4,y4) for a rectangle
      return null;
    }

    const points = [];
    // Process all 4 points of the rectangle
    for (let i = 0; i < 8; i += 2) {
      // Convert PDF coordinates to viewport coordinates
      const x = polygonPoints[i] * 72 * viewport.scale;
      const y = polygonPoints[i + 1] * 72 * viewport.scale;
      points.push(`${x},${y}`);
    }

    const polygon = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'polygon'
    );
    polygon.setAttribute('points', points.join(' '));

    const isHighConfidence = confidence >= confidenceThreshold;
    const color = isHighConfidence
      ? 'rgba(0, 255, 0, 0.2)'
      : 'rgba(255, 0, 0, 0.2)';
    const strokeColor = isHighConfidence
      ? 'rgba(0, 255, 0, 0.5)'
      : 'rgba(255, 0, 0, 0.5)';

    polygon.setAttribute('fill', color);
    polygon.setAttribute('stroke', strokeColor);
    polygon.setAttribute('stroke-width', '2');
    polygon.setAttribute('vector-effect', 'non-scaling-stroke');

    return polygon;
  };

  // Render the indicator arrow
  const renderTransactionIndicator = (
    viewport,
    canvasOffset,
    data
  ) => {
    if (!selectedTransactionId) return;

    // Handle .NET style JSON responses
    const accounts = (data && data.accounts && data.accounts.$values) || (data && data.accounts) || [];

    if (!accounts || accounts.length === 0) {
      console.log('No accounts array found for transaction indicator');
      return;
    }

    // Find selected transaction across all accounts
    let selectedTransaction = null;
    // eslint-disable-next-line
    for (const account of accounts) {
      const transactions = (account.transactions && account.transactions.$values) || account.transactions || [];
      const matchingTransaction = transactions.find(t => Number(t.id) === Number(selectedTransactionId));
      if (matchingTransaction) {
        selectedTransaction = matchingTransaction;
        break;
      }
    }

    if (!selectedTransaction) {
      console.log(`Transaction ID ${selectedTransactionId} not found in any account`);
      return;
    }

    // Try each location type
    const txAny = selectedTransaction;
    const dateLocation = normalizeLocation(selectedTransaction.dateLocation || txAny.DateLocation);

    const descLocation = normalizeLocation(selectedTransaction.descriptionLocation || txAny.DescriptionLocation);

    const depositLocation = normalizeLocation(selectedTransaction.depositAmountLocation || txAny.DepositAmountLocation);

    const withdrawalLocation = normalizeLocation(selectedTransaction.withdrawalAmountLocation || txAny.WithdrawalAmountLocation);

    const location = dateLocation || descLocation || depositLocation || withdrawalLocation;

    // Check if we have any valid location and if it's on the current page
    if (!location || !location.polygon || location.pageNumber !== currentPage) {
      return;
    }

    const { polygon } = location;

    // For a rectangle, calculate the midpoint of the left edge (between points 0,1 and 6,7)
    const x1 = polygon[0] * 72 * viewport.scale;
    const y1 = polygon[1] * 72 * viewport.scale;
    const x4 = polygon[6] * 72 * viewport.scale;
    const y4 = polygon[7] * 72 * viewport.scale;

    const midX = (x1 + x4) / 2;
    const midY = (y1 + y4) / 2;

    // Create SVG for the indicator
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'transaction-indicator');
    svg.setAttribute('width', viewport.width.toString());
    svg.setAttribute('height', viewport.height.toString());
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = `${canvasOffset} px`;
    svg.style.pointerEvents = 'none';
    svg.style.zIndex = '2'; // Higher than highlights

    // Create the triangle pointing to the transaction
    const triangle = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path'
    );

    // Calculate triangle points (20px wide, 30px long)
    const arrowWidth = 10;
    const arrowLength = 30;
    const pointX = midX - arrowLength; // Point towards the left edge

    const trianglePath = `
    M ${midX} ${midY}
    L ${pointX} ${midY - arrowWidth}
    L ${pointX} ${midY + arrowWidth}
    Z
  `;

    triangle.setAttribute('d', trianglePath);
    triangle.setAttribute('fill', '#FF0000');
    triangle.setAttribute('opacity', '0.8');

    // Add animation
    triangle.innerHTML = `
    <animate
      attributeName="opacity"
      values="0.8;0.4;0.8"
      dur="1s"
      repeatCount="indefinite"
    />
  `;

    svg.appendChild(triangle);

    // Add to the container
    const container = canvasRef.current && canvasRef.current.parentElement;
    if (container) {
      // Remove any existing indicators
      const existingIndicators = document.querySelectorAll('.transaction-indicator');
      existingIndicators.forEach(indicator => indicator.remove());

      container.style.position = 'relative';
      container.appendChild(svg);
    }
  };

  const renderHighlights = (
    viewport,
    canvasOffset,
    data
  ) => {
    // console.log('Render highlights data:', data);

    if (!showConfidenceHighlights) {
      return;
    }

    // Ensure we have valid accounts array
    const accounts = data && Array.isArray(data.accounts) ? data.accounts : [];

    if (accounts.length === 0) {
      console.log('No accounts found in data');
      return;
    }

    // Remove existing highlights
    cleanupHighlights();

    // Create SVG container
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'highlights-container');
    svg.setAttribute('width', viewport.width.toString());
    svg.setAttribute('height', viewport.height.toString());
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = `${canvasOffset} px`;
    svg.style.pointerEvents = 'none';
    svg.style.zIndex = '1';

    // Use type assertion to access potential PascalCase properties
    const dataAny = data;

    // Process static fields for current page
    const staticFields = [
      {
        name: 'bankName',
        location: normalizeLocation(data.bankNameLocation
          || dataAny.BankNameLocation),
        confidence: data.bankNameConfidence,
      },
      {
        name: 'bankAddress',
        location: normalizeLocation(data.bankAddressLocation
          || dataAny.BankAddressLocation),
        confidence: data.bankAddressConfidence,
      },
      {
        name: 'accountHolderName',
        location: normalizeLocation(data.accountHolderNameLocation
          || dataAny.AccountHolderNameLocation),
        confidence: data.accountHolderNameConfidence,
      },
      {
        name: 'accountHolderAddress',
        location: normalizeLocation(data.accountHolderAddressLocation
          || dataAny.AccountHolderAddressLocation),
        confidence: data.accountHolderAddressConfidence,
      },
    ];

    // Process static fields
    staticFields.forEach((field) => {
      if (
        field.location
        && field.location.pageNumber === currentPage
        && field.location.polygon
      ) {
        const polygon = createPolygonElement(
          field.location.polygon,
          field.confidence || 0,
          viewport
        );
        if (polygon) svg.appendChild(polygon);
      }
    });

    // Process all accounts and their fields
    accounts.forEach((account) => {
      if (!account) return;

      // Type assertion for potential PascalCase properties
      const accountAny = account;

      // Process account fields
      const accountFields = [
        {
          name: 'accountNumber',
          location: normalizeLocation(account.accountNumberLocation
            || accountAny.AccountNumberLocation),
          confidence: account.accountNumberConfidence,
        },
        {
          name: 'accountType',
          location: normalizeLocation(account.accountTypeLocation
            || accountAny.AccountTypeLocation),
          confidence: account.accountTypeConfidence,
        },
        {
          name: 'beginningBalance',
          location: normalizeLocation(account.beginningBalanceLocation
            || accountAny.BeginningBalanceLocation),
          confidence: account.beginningBalanceConfidence,
        },
        {
          name: 'endingBalance',
          location: normalizeLocation(account.endingBalanceLocation
            || accountAny.EndingBalanceLocation),
          confidence: account.endingBalanceConfidence,
        },
      ];

      // Process account fields
      accountFields.forEach((field) => {
        if (
          field.location
          && field.location.pageNumber === currentPage
          && field.location.polygon
        ) {
          const polygon = createPolygonElement(
            field.location.polygon,
            field.confidence || 0,
            viewport
          );
          if (polygon) svg.appendChild(polygon);
        }
      });

      // Process transactions
      const transactions = Array.isArray(account.transactions)
        ? account.transactions
        : [];
      transactions.forEach((transaction) => {
        if (!transaction) return;

        // Type assertion for potential PascalCase properties
        const txAny = transaction;

        const transactionFields = [
          {
            name: 'transaction_date',
            location: normalizeLocation(transaction.dateLocation
              || txAny.DateLocation),
            confidence: transaction.dateConfidence,
          },
        ];

        if (transaction.depositAmount !== null) {
          transactionFields.push({
            name: 'transaction_deposit',
            location: normalizeLocation(transaction.depositAmountLocation
              || txAny.DepositAmountLocation),
            confidence: transaction.depositAmountConfidence,
          });
        }

        if (transaction.withdrawalAmount !== null) {
          transactionFields.push({
            name: 'transaction_withdrawal',
            location: normalizeLocation(transaction.withdrawalAmountLocation
              || txAny.WithdrawalAmountLocation),
            confidence: transaction.withdrawalAmountConfidence,
          });
        }

        transactionFields.forEach((field) => {
          if (
            field.location
            && field.location.pageNumber === currentPage
            && field.location.polygon
          ) {
            const polygon = createPolygonElement(
              field.location.polygon,
              field.confidence || 0,
              viewport
            );
            if (polygon) svg.appendChild(polygon);
          }
        });
      });
    });

    // Add SVG to container if it has highlights
    if (svg.children.length > 0) {
      const container = canvasRef.current && canvasRef.current.parentElement;
      if (container) {
        container.style.position = 'relative';
        container.appendChild(svg);
      }
    }
  };

  const renderPage = async () => {
    if (!pdfDocument || !canvasRef.current) return;

    // If already rendering, don't start a new render
    if (renderMutexRef.current) {
      // console.log('Render already in progress, waiting');
      return;
    }

    renderMutexRef.current = true;

    try {
      setIsLoading(true);

      // Cancel any existing render task properly
      if (renderTask) {
        try {
          const task = renderTask;
          setRenderTask(null); // Clear reference first
          task.cancel();
          // Don't await the cancelled promise as it will reject
        } catch (err) {
          console.log('Error cancelling previous render task:', err);
        }
      }

      // Make sure cleanup is complete
      cleanupHighlights();
      const existingIndicators = document.querySelectorAll('.transaction-indicator');
      existingIndicators.forEach(indicator => indicator.remove());

      // Get page and prepare canvas
      const page = await pdfDocument.getPage(currentPage);
      const canvas = canvasRef.current;
      const viewport = page.getViewport({ scale: zoomLevel });

      // Set canvas dimensions - this is important for correct rendering
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const canvasContext = canvas.getContext('2d');
      if (!canvasContext) return;

      // Explicitly clear the canvas to ensure a clean state
      canvasContext.clearRect(0, 0, canvas.width, canvas.height);

      // Create a new render task with transformed viewport
      const newRenderTask = page.render({
        canvasContext,
        viewport,
        intent: 'display', // Ensure we're using display intent
      });

      // Store reference to the new render task
      setRenderTask(newRenderTask);

      // Wait for rendering to complete
      await newRenderTask.promise;

      // Ensure the render completed successfully before adding highlights
      if (canvasRef.current && !newRenderTask.cancelled) {
        const canvasRect = canvas.getBoundingClientRect();
        const parentRect = canvas.parentElement && canvas.parentElement.getBoundingClientRect();
        const horizontalOffset = parentRect
          ? canvasRect.left - parentRect.left
          : 0;

        // Add highlights and indicators after successful page render
        if (jsonData) {
          renderHighlights(viewport, horizontalOffset, jsonData);
        }

        if (selectedTransactionId && jsonData) {
          renderTransactionIndicator(
            viewport,
            horizontalOffset,
            jsonData
          );

          // Only scroll if we're still on the same page
          if (currentPage === lastPageRef.current) {
            scrollToTransactionIndicator();
          }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'RenderingCancelledException') {
        console.log('Rendering cancelled');
      } else {
        console.error('Error rendering page:', err);
      }
    } finally {
      setIsLoading(false);
      // Release the mutex
      renderMutexRef.current = false;
    }
  };

  // Navigate to transaction's page when selected
  useEffect(() => {
    if (!selectedTransactionId || !jsonData) return;

    // Handle .NET-style JSON responses where arrays might be in $values property
    const accounts = jsonData
      && jsonData.accounts
      && (jsonData.accounts.$values
      || (Array.isArray(jsonData.accounts)) ? jsonData.accounts : []);

    if (!accounts || accounts.length === 0) return;

    // Find the selected transaction across all accounts
    let foundTransaction = null;
    let transactionPage = null;

    // eslint-disable-next-line
    for (const account of accounts) {
      const transactions = account.transactions ? (account.transactions.$values || account.transactions) : [];
      // eslint-disable-next-line
      if (!transactions || !Array.isArray(transactions)) continue;

      const transaction = transactions.find(t => Number(t.id) === Number(selectedTransactionId));
      if (transaction) {
        foundTransaction = transaction;

        // Determine which page the transaction is on
        const txAny = transaction;
        const dateLocation = normalizeLocation(transaction.dateLocation || txAny.DateLocation);
        const descLocation = normalizeLocation(transaction.descriptionLocation || txAny.DescriptionLocation);
        const depositLocation = normalizeLocation(transaction.depositAmountLocation || txAny.DepositAmountLocation);
        const withdrawalLocation = normalizeLocation(transaction.withdrawalAmountLocation || txAny.WithdrawalAmountLocation);

        transactionPage = (dateLocation && dateLocation.pageNumber)
          || (descLocation && descLocation.pageNumber)
          || (depositLocation && depositLocation.pageNumber)
          || (withdrawalLocation && withdrawalLocation.pageNumber);
        break;
      }
    }

    // If transaction found and it's on a different page, navigate to that page
    if (
      foundTransaction
      && transactionPage
      && transactionPage !== currentPage
    ) {
      handlePageChange(transactionPage);
    } else if (foundTransaction && transactionPage === currentPage) {
      // If we're already on the correct page, just update the highlights and indicator
      renderPage();
    }
  }, [selectedTransactionId, jsonData]);

  useEffect(() => {
    lastPageRef.current = currentPage;
  }, [currentPage]);

  // Effect to render the page when dependencies change
  // Effect for page rendering
  useEffect(() => {
    // In case we're stuck in a rendering state, reset after a timeout
    const resetMutexTimer = setTimeout(() => {
      if (renderMutexRef.current) {
        console.log('Resetting stuck render mutex');
        renderMutexRef.current = false;
      }
    }, 5000); // Safety timeout

    const renderTimer = setTimeout(() => {
      renderPage();
    }, 10); // Short delay to allow state to settle

    return () => {
      clearTimeout(renderTimer);
      clearTimeout(resetMutexTimer);

      // If there's a render task, cancel it when dependencies change
      if (renderTask) {
        try {
          renderTask.cancel();
        } catch (err) {
          console.log('Error in cleanup:', err);
        }
      }
    };
  }, [
    pdfDocument,
    currentPage,
    zoomLevel,
    showConfidenceHighlights,
    jsonData,
    selectedTransactionId,
  ]);

  // MM: this is just for debug purposes
  /*
  useEffect(() => {
    console.log('Current page:', currentPage);
  }, [currentPage]);
*/
  // Effect for confidence highlights
  useEffect(() => {
    if (!showConfidenceHighlights) {
      cleanupHighlights();
    }
  }, [showConfidenceHighlights]);

  return (
    <div
      className="flex flex-col h-full"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      {error ? (
        <div className="text-red-500 p-4">{error}</div>
      ) : (
        <Fragment>
          <PDFControls
            currentPage={currentPage}
            totalPages={totalPages}
            zoomLevel={zoomLevel}
            isLoading={isLoading}
            showHighlights={showConfidenceHighlights}
            onPageChange={page => handlePageChange(page)}
            onZoomChange={setZoomLevel}
            onHighlightsToggle={setShowConfidenceHighlights}
          />

          <div className="flex-1 relative">
            <div className="absolute inset-0 overflow-auto">
              <div className="min-h-full flex items-start justify-center">
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/75 z-10">
                    <div className="w-8 h-8 border-4 border-gray-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                <div className="relative" style={{ width: 'fit-content' }}>
                  <canvas ref={canvasRef} style={{ display: 'block' }} />
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}
    </div>
  );
};

PDFViewer.propTypes = {
  bankProcessorKey: PropTypes.string.isRequired,
  applicationId: PropTypes.string.isRequired,
  statementId: PropTypes.string.isRequired,
  selectedTransactionId: PropTypes.number,
  onPageChange: PropTypes.func,
  currentPage: PropTypes.number,
};

export default PDFViewer;
